import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageFrame } from '../components/work/page-chrome';
import { PageTitle } from '../components/work/page-states';
import RichTextField from '../components/work/RichTextField';
import { useOverlays } from '../components/work/overlay-context';
import { useWindowKeydown } from '../components/use-window-keydown';
import { apiFetch, v1Path } from '../lib/api';
import { uploadPendingAttachment } from '../lib/attachments';
import {
  OPS_CREATE,
  descriptionForCreate,
  isUnusedOpsDescription,
  type OpsItemType,
} from '../lib/ops-record';
import { getLastProjectId, isProjectId, setLastProjectId } from '../lib/project-path';
import { isWorkspaceProject } from '../lib/workspace-projects';
import { isRichTextEmpty } from '../lib/rich-text';
import { UUID_RE, type ProjectDto, type StateDto, type WorkItemFull } from '../lib/work-types';

function queryId(raw: string | null, kind: 'project' | 'state'): string | null {
  if (!raw) return null;
  if (kind === 'project') return isProjectId(raw) ? raw : null;
  return UUID_RE.test(raw) ? raw : null;
}

export default function NewOpsItem({ type }: { type: OpsItemType }) {
  const copy = OPS_CREATE[type];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirm, toast, isBlocked } = useOverlays();
  const requestedProjectId = queryId(searchParams.get('project'), 'project');
  const requestedStateId = queryId(searchParams.get('state'), 'state');

  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectId, setProjectId] = useState(requestedProjectId ?? '');
  const [states, setStates] = useState<StateDto[]>([]);
  const [stateId, setStateId] = useState(requestedStateId ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(copy.descriptionTemplate);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const descriptionDirty =
    !isUnusedOpsDescription(type, description) && !isRichTextEmpty(description);
  const dirty = title.trim() !== '' || descriptionDirty;

  useEffect(() => {
    setTitle('');
    setDescription(copy.descriptionTemplate);
  }, [copy.descriptionTemplate]);

  useEffect(() => {
    let cancelled = false;
    void apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'))
      .then((res) => {
        if (cancelled) return;
        const members = res.items.filter((project) => project.is_member !== false && !isWorkspaceProject(project));
        setProjects(members);
        setLoadError(members.length === 0 ? copy.joinError : null);
        setProjectId((current) => {
          if (current && members.some((project) => project.id === current)) return current;
          const last = getLastProjectId();
          const incidents = members.find((project) => project.identifier.toUpperCase() === 'INC');
          const preferred =
            incidents ?? members.find((project) => project.id === last) ?? members[0];
          return preferred?.id ?? '';
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setProjects([]);
        setLoadError(err instanceof Error ? err.message : 'Failed to load streams');
      });
    return () => {
      cancelled = true;
    };
  }, [copy.joinError]);

  useEffect(() => {
    if (!projectId) {
      setStates([]);
      setStateId('');
      return;
    }
    let cancelled = false;
    void apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`))
      .then((res) => {
        if (cancelled) return;
        setStates(res.items);
        setStateId((current) => {
          if (current && res.items.some((state) => state.id === current)) return current;
          if (requestedStateId && res.items.some((state) => state.id === requestedStateId)) {
            return requestedStateId;
          }
          return res.items.find((state) => state.is_default)?.id ?? res.items[0]?.id ?? '';
        });
      })
      .catch(() => {
        if (!cancelled) {
          setStates([]);
          setStateId('');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, requestedStateId]);

  async function leave() {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard draft?',
        description: 'You have unsaved fields. Leave without creating?',
        confirmLabel: 'Discard',
        destructive: true,
      });
      if (!ok) return;
    }
    navigate(copy.listPath);
  }

  useWindowKeydown((event) => {
    if (event.key !== 'Escape' || isBlocked || busy) return;
    event.preventDefault();
    void leave();
  });

  async function submit() {
    const trimmed = title.trim();
    if (!trimmed) {
      toast(copy.missingTitle);
      return;
    }
    if (!isProjectId(projectId)) {
      toast(copy.missingProject);
      return;
    }
    setBusy(true);
    try {
      const created = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items`), {
        method: 'POST',
        body: JSON.stringify({
          title: trimmed,
          description: descriptionForCreate(type, description),
          type,
          ...(stateId && UUID_RE.test(stateId) ? { state_id: stateId } : {}),
        }),
      });
      if (!created.id || !created.project_id) {
        throw new Error(copy.missingId);
      }
      setLastProjectId(created.project_id);
      toast(`Created ${created.key}`);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      navigate(`/projects/${created.project_id}/work-items/${created.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : copy.createFailed);
    } finally {
      setBusy(false);
    }
  }

  const titleId = `${copy.fieldId}-title`;
  const projectFieldId = `${copy.fieldId}-project`;
  const stateFieldId = `${copy.fieldId}-state`;

  return (
    <PageFrame>
      <div className="mx-auto max-w-5xl">
        <Link
          to={copy.listPath}
          onClick={(event) => {
            if (!dirty) return;
            event.preventDefault();
            void leave();
          }}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          {copy.listLabel}
        </Link>

        <PageTitle
          title={copy.pageTitle}
          action={
            <div className="flex shrink-0 gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => void leave()}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={busy || !title.trim() || !projectId}
                onClick={() => void submit()}
              >
                {busy ? 'Creating…' : copy.createLabel}
              </Button>
            </div>
          }
        />

        {loadError ? <p className="mb-4 text-sm text-destructive">{loadError}</p> : null}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_16rem] lg:items-start lg:gap-12">
          <div className="space-y-4">
            <Input
              id={titleId}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={copy.titlePlaceholder}
              aria-label={copy.titleAriaLabel}
              autoFocus
              className="h-auto border-transparent px-2 py-1 text-lg font-semibold hover:border-input focus:border-input"
            />
            <RichTextField
              key={type}
              variant="full"
              value={description}
              upload={(file) => uploadPendingAttachment(file).then((row) => row.download_url)}
              onUploadError={toast}
              onChange={setDescription}
              className="[&_.ProseMirror]:min-h-[20rem]"
            />
          </div>

          <aside className="mt-8 space-y-4 lg:mt-0">
            <div className="space-y-1">
              <Label htmlFor={projectFieldId}>Stream</Label>
              <Select value={projectId} onValueChange={setProjectId} disabled={projects.length === 0}>
                <SelectTrigger id={projectFieldId}>
                  <SelectValue placeholder="Select a stream" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.identifier} · {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {states.length > 0 && stateId ? (
              <div className="space-y-1">
                <Label htmlFor={stateFieldId}>State</Label>
                <Select value={stateId} onValueChange={setStateId}>
                  <SelectTrigger id={stateFieldId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state.id} value={state.id}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </PageFrame>
  );
}
